import React, { useState, useEffect } from 'react';
import { auth, database } from '../firebaseConfig';
import {
  signInWithPopup,
  GoogleAuthProvider,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
  sendEmailVerification,
} from 'firebase/auth';
import {
  getAuth,
  setPersistence,
  browserLocalPersistence,
  onAuthStateChanged,
} from 'firebase/auth';
import { ref, set, get } from 'firebase/database';
import { useNavigate } from 'react-router-dom';
import googleLogo from './images/google-logo.png';

const errorMessages = {
  'auth/invalid-email': 'Please enter a valid email address.',
  'auth/user-disabled': 'This user has been disabled. Please contact support.',
  'auth/user-not-found': 'No account found with this email. Please sign up.',
  'auth/wrong-password': 'Incorrect password. Please try again.',
  'auth/email-already-in-use': 'An account with this email already exists. Please log in.',
  'auth/network-request-failed': 'Network error. Please check your internet connection.',
  default: 'An unexpected error occurred. Please try again.',
};

const getFriendlyErrorMessage = (error) => errorMessages[error.code] || errorMessages.default;

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [showReset, setShowReset] = useState(false);
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(true);
  const provider = new GoogleAuthProvider();
  const navigate = useNavigate();

  useEffect(() => {
    document.title = 'Log In - Professional System';
    const authInstance = getAuth();

    setPersistence(authInstance, browserLocalPersistence)
      .then(() => {
        const unsubscribe = onAuthStateChanged(authInstance, (user) => {
          if (user) {
            const userRef = ref(database, `users/${user.uid}`);
            get(userRef)
              .then((snapshot) => {
                if (snapshot.exists()) {
                  const userData = snapshot.val();
                  const redirectPage = userData.isAdmin ? '/admin' : '/student';
                  navigate(redirectPage);
                }
              })
              .catch((error) => console.error('Error checking user role', error));
          }
          setLoading(false);
        });
        return unsubscribe;
      })
      .catch((error) => console.error('Error setting persistence', error));
  }, [navigate]);

  const handleSignInWithGoogle = async () => {
    try {
      const result = await signInWithPopup(auth, provider);
      const user = result.user;
  
      const userRef = ref(database, `users/${user.uid}`);
      get(userRef)
        .then((snapshot) => {
          if (!snapshot.exists()) {
            // Create a new user in the database
            set(userRef, {
              email: user.email,
              name: user.displayName,
              photoURL: user.photoURL,
              lastLogin: new Date().toISOString(),
              isAdmin: false,
              isStudent: false,
              emailVerified: true, // Automatically mark email as verified
            });
          } else {
            // Update the user's `emailVerified` status in the database if needed
            const userData = snapshot.val();
            if (!userData.emailVerified) {
              set(userRef, { ...userData, emailVerified: true });
            }
          }
          const userData = snapshot.val();
          const redirectPage = userData?.isAdmin ? '/admin' : '/student';
          navigate(redirectPage);
        })
        .catch((err) => setError(getFriendlyErrorMessage(err)));
    } catch (err) {
      setError(getFriendlyErrorMessage(err));
    }
  };
  
  const handleEmailPasswordSignIn = async () => {
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      if (!user.emailVerified) {
        await sendEmailVerification(user);
        setError('Please verify your email address. A verification email has been sent.');
        return;
      }

      const userRef = ref(database, `users/${user.uid}`);
      get(userRef)
        .then((snapshot) => {
          if (snapshot.exists()) {
            const redirectPage = snapshot.val().isAdmin ? '/admin' : '/student';
            navigate(redirectPage);
          }
        })
        .catch((error) => console.error('Error fetching user data', error));
    } catch (err) {
      setError(getFriendlyErrorMessage(err));
    }
  };

  const handleSignUp = async () => {
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      await sendEmailVerification(user);

      set(ref(database, `users/${user.uid}`), {
        email: user.email,
        createdOn: new Date().toISOString(),
        emailVerified: false,
        isAdmin: false,
        isStudent: false,
      });

      setError('Sign-up successful! A verification email has been sent to your inbox.');
      setIsLogin(true);
    } catch (err) {
      setError(getFriendlyErrorMessage(err));
    }
  };

  const handlePasswordReset = async () => {
    if (!email) {
      setError('Please enter your email address to reset your password.');
      return;
    }

    try {
      await sendPasswordResetEmail(auth, email);
      setError('Password reset email sent. Check your inbox.');
      setShowReset(false);
    } catch (err) {
      setError(getFriendlyErrorMessage(err));
    }
  };

  if (loading) return <div>Loading...</div>;

  return (
    <div className="d-flex justify-content-center align-items-center vh-100">
      <div className="card shadow-lg p-4" style={{ maxWidth: '400px', width: '100%' }}>
        <h2 className="text-center mb-4">
          <i className="bi bi-person-circle me-2"></i>
          {isLogin ? 'Welcome Back!' : 'Create an Account'}
        </h2>

        <button onClick={handleSignInWithGoogle} className="btn btn-outline-danger w-100 mb-3">
          <img src={googleLogo} alt="Google Logo" className="me-2" style={{ width: '20px' }} />
          Sign in with Google
        </button>

        <div className="text-center mb-4">
          <p className="text-muted mb-0">or</p>
        </div>

        <form>
        <div className="form-group mb-3">
            <label htmlFor="email" className="sr-only">Email</label>
            <div className="input-group">
              <div className="input-group-prepend">
                <span className="input-group-text bg-light"><i className="bi bi-envelope"></i></span>
              </div>
            <input
              type="email"
              className="form-control pl-4"
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
         </div>
         </div>

          {!showReset && (
            <div className="form-group mb-3">
              <label htmlFor="password" className="sr-only">Password</label>
              <div className="input-group">
                <div className="input-group-prepend">
                  <span className="input-group-text bg-light"><i className="bi bi-lock"></i></span>
                </div>
                    <input
                type="password"
                className="form-control pl-4"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
             </div>
             </div>
          )}

          <button
            type="button"
            onClick={showReset ? handlePasswordReset : isLogin ? handleEmailPasswordSignIn : handleSignUp}
            className="btn btn-primary w-100 mt-3"
          >
            {showReset ? (
              <>
                <i className="bi bi-key"></i> Reset Password
              </>
            ) : isLogin ? (
              <>
                <i className="bi bi-box-arrow-in-right"></i> Login
              </>
            ) : (
              <>
                <i className="bi bi-person-plus"></i> Sign Up
              </>
            )}
          </button>
        </form>

        <div className="text-center mt-3">
          <button className="btn btn-link" onClick={() => setIsLogin(!isLogin)}>
            {isLogin ? 'Create an account' : 'Already have an account? Login'}
          </button>
          {isLogin && (
            <button className="btn btn-link" onClick={() => setShowReset(!showReset)}>
              {showReset ? 'Back to Login' : 'Forgot Password?'}
            </button>
          )}
        </div>

        {error && <p className="text-danger text-center mt-3">{error}</p>}
      </div>
    </div>
  );
};

export default LoginPage;




